import { useTranslation } from 'react-i18next';

import PenFilledIcon from '../../../../assets/icons/PenFilled';
import PenOutlineIcon from '../../../../assets/icons/PenOutline';
import useSidebarContext from '../../../../hooks/sidebar/useSidebarContext';
import { useFootprintAndProduct } from '../../../../hooks/useFootprintAndProduct';
import { Locale } from '../../../../i18n';
import CrossSectoralStandards from '../../../../shared/components/cross-sectoral-standards/CrossSectoralStandards';
import CustomIcon from '../../../../shared/components/custom-icon/CustomIcon';
import ProductOrSectorSpecificRules from '../../../../shared/components/product-or-sector-specific-rules/ProductOrSectorSpecificRules';
import SecondaryEmissionsFactorSources from '../../../../shared/components/secondary-emissions-factor-sources/SecondaryEmissionsFactorSources';
import { parseDateWithoutTime } from '../../../../shared/helpers/parse-date';
import {
  AllocationWasteIncinerationCharacteristicToLabelMap,
  packagingEmissionValueToLabelKey,
} from '../../../../shared/interfaces/IFootprint';
import DetailRow from '../detail-row/DetailRow';

const AdditionalDetailsSection = (): JSX.Element => {
  const {
    t,
    i18n: { language },
  } = useTranslation();
  const { openSidebar } = useSidebarContext();
  const {
    footprintDetails,
    isFootprintLoading: isLoading,
    showEditButton,
  } = useFootprintAndProduct();

  return (
    <div className='bg-white p-4 md:p-5 border-round-2xl flex flex-column w-full'>
      {/* Title */}
      <div className='flex flex-row gap-3 align-items-center pb-3'>
        <div className='gap-2 flex'>
          <div className='bg-primary p-1' />
          <h5 className='m-0 p-0 font-medium'>
            {t('footprintDetailsPage.sections.additionalDetailsSection.title')}
          </h5>
        </div>
        {showEditButton && (
          <CustomIcon
            icon={PenOutlineIcon}
            hoverIcon={PenFilledIcon}
            showTextOnHover
            hoverText={t('global.edit')}
            hoverTextColor='primary'
            className='cursor-pointer flex justify-content-center align-items-center'
            onClick={() => {
              openSidebar('edit-additional-details-sidebar', {
                position: 'right',
                footprintDetails,
              });
            }}
          />
        )}
      </div>

      {/* Cross Sectoral Standard */}
      <DetailRow
        label={t('footprintDetailsPage.sections.additionalDetailsSection.crossSectoralStandard')}
        isLoading={isLoading}
      >
        <CrossSectoralStandards
          standards={footprintDetails?.pcf?.cross_sectoral_standards_used ?? []}
        />
      </DetailRow>

      {/* Product- or Sector-specific Rules */}
      <DetailRow
        label={t(
          'footprintDetailsPage.sections.additionalDetailsSection.productOrSectorSpecificRules',
        )}
        labelClassName={`font-medium ${
          footprintDetails?.pcf?.product_or_sector_specific_rules ? 'w-full' : ''
        }`}
        isLoading={isLoading}
      >
        <ProductOrSectorSpecificRules
          rules={footprintDetails?.pcf?.product_or_sector_specific_rules ?? []}
        />
      </DetailRow>

      {/* Secondary Emissions Factor Sources */}
      <DetailRow
        label={t(
          'footprintDetailsPage.sections.additionalDetailsSection.secondaryEmissionsFactorSources',
        )}
        isLoading={isLoading}
      >
        <SecondaryEmissionsFactorSources
          sources={footprintDetails?.pcf?.secondary_emission_factor_sources ?? []}
        />
      </DetailRow>

      {/* Mass Balancing Used */}
      <DetailRow
        label={t('footprintDetailsPage.sections.additionalDetailsSection.massBalancingUsed')}
        isLoading={isLoading}
        showDivider={footprintDetails?.pcf?.mass_balancing_used !== true}
      >
        <span className='font-light text-black-alpha-60'>
          {footprintDetails?.pcf?.mass_balancing_used == null
            ? t('global.notSpecified')
            : t(
                footprintDetails?.pcf?.mass_balancing_used
                  ? 'footprintDetailsPage.sections.additionalDetailsSection.massBalancingUsedYesLabel'
                  : 'footprintDetailsPage.sections.additionalDetailsSection.massBalancingUsedNoLabel',
              )}
        </span>
      </DetailRow>

      {/* Mass Balancing Used - subfields */}
      {footprintDetails?.pcf?.mass_balancing_used === true && (
        <>
          {/* Free Attribution */}
          <DetailRow
            label={t('footprintDetailsPage.sections.additionalDetailsSection.freeAttribution')}
            labelClassName='font-medium ml-4'
            isLoading={isLoading}
            showDivider={false}
          >
            <span className='font-light text-black-alpha-60'>
              {footprintDetails?.pcf?.free_attribution_in_mass_balancing == null
                ? t('global.notSpecified')
                : t(
                    footprintDetails?.pcf?.free_attribution_in_mass_balancing
                      ? 'global.yes'
                      : 'global.no',
                  )}
            </span>
          </DetailRow>

          {/* Certificate Scheme */}
          <DetailRow
            label={t('footprintDetailsPage.sections.additionalDetailsSection.certificateScheme')}
            labelClassName='font-medium ml-4'
            isLoading={isLoading}
          >
            <span className='font-light text-black-alpha-60'>
              {footprintDetails?.pcf?.mass_balancing_certificate_scheme ?? t('global.notSpecified')}
            </span>
          </DetailRow>
        </>
      )}

      {/* CCS/BECCS */}
      <DetailRow
        label={t('footprintDetailsPage.sections.additionalDetailsSection.ccsBeccs')}
        isLoading={isLoading}
      >
        <span className='font-light text-black-alpha-60'>
          {footprintDetails?.pcf?.ccs_technological_co2_capture_included == null
            ? t('global.notSpecified')
            : t(
                footprintDetails?.pcf?.ccs_technological_co2_capture_included
                  ? 'footprintDetailsPage.sections.additionalDetailsSection.ccsBeccsApplied'
                  : 'footprintDetailsPage.sections.additionalDetailsSection.ccsBeccsNotApplied',
              )}
        </span>
      </DetailRow>

      {/* Characterization Factors */}
      <DetailRow
        label={t('footprintDetailsPage.sections.additionalDetailsSection.characterizationFactors')}
        isLoading={isLoading}
      >
        <span className='font-light text-black-alpha-60'>
          {footprintDetails?.pcf?.characterization_factors ?? t('global.notSpecified')}
        </span>
      </DetailRow>

      {/* Allocation Waste Incineration */}
      <DetailRow
        label={t(
          'footprintDetailsPage.sections.additionalDetailsSection.allocationWasteIncineration',
        )}
        isLoading={isLoading}
      >
        <span className='font-light text-black-alpha-60'>
          {footprintDetails?.pcf?.allocation_waste_incineration
            ? AllocationWasteIncinerationCharacteristicToLabelMap[
                footprintDetails?.pcf?.allocation_waste_incineration
              ]
            : t('global.notSpecified')}
        </span>
      </DetailRow>

      {/* Exempted Emisions */}
      <DetailRow
        label={t('footprintDetailsPage.sections.additionalDetailsSection.exemptedEmissions')}
        isLoading={isLoading}
      >
        <span className='font-light text-black-alpha-60'>
          {footprintDetails?.pcf?.exempted_emissions_percent != null
            ? `${footprintDetails?.pcf?.exempted_emissions_percent}%`
            : t('global.notSpecified')}
        </span>
      </DetailRow>

      {/* Packaging Emissions */}
      <DetailRow
        label={t('footprintDetailsPage.sections.additionalDetailsSection.packagingEmissions')}
        isLoading={isLoading}
      >
        <span className='font-light text-black-alpha-60'>
          {t(
            packagingEmissionValueToLabelKey(
              String(footprintDetails?.pcf?.packaging_emissions_included),
            ),
          )}
        </span>
      </DetailRow>

      {/* Distribution Emissions */}
      <DetailRow
        label={t('footprintDetailsPage.sections.additionalDetailsSection.distributionEmissions')}
        isLoading={isLoading}
      >
        <span className='font-light text-black-alpha-60'>
          {t(
            packagingEmissionValueToLabelKey(
              String(footprintDetails?.pcf?.distribution_stage_included),
            ),
          )}
        </span>
      </DetailRow>

      {/* Partial or Full Pcf */}
      <DetailRow
        label={t('footprintDetailsPage.sections.additionalDetailsSection.partialOrFullPcf')}
        isLoading={isLoading}
      >
        <span className='font-light text-black-alpha-60'>
          {footprintDetails?.partial_full_pcf ?? t('global.notSpecified')}
        </span>
      </DetailRow>

      {/* Reference Period */}
      <DetailRow
        label={t('footprintDetailsPage.sections.additionalDetailsSection.referencePeriod')}
        isLoading={isLoading}
      >
        <span className='font-light text-black-alpha-60'>
          {footprintDetails?.pcf?.reference_period_start &&
          footprintDetails?.pcf?.reference_period_end ? (
            <div className='flex flex-row gap-1'>
              <i className='pi pi-calendar' />
              {`${parseDateWithoutTime(
                footprintDetails?.pcf?.reference_period_start,
                language as Locale,
              )} - ${parseDateWithoutTime(
                footprintDetails?.pcf?.reference_period_end,
                language as Locale,
              )}`}
            </div>
          ) : (
            t('global.notSpecified')
          )}
        </span>
      </DetailRow>

      {/* Valid Until - validity period end */}
      <DetailRow
        label={t('footprintDetailsPage.sections.additionalDetailsSection.validUntil')}
        isLoading={isLoading}
      >
        <span className='font-light text-black-alpha-60'>
          {footprintDetails?.validity_period_end ? (
            <div className='flex flex-row gap-1'>
              <i className='pi pi-calendar' />
              {parseDateWithoutTime(footprintDetails.validity_period_end, language as Locale)}
            </div>
          ) : (
            t('global.notSpecified')
          )}
        </span>
      </DetailRow>

      {/* Disclaimer - pcf legal statement */}
      <DetailRow
        label={t('footprintDetailsPage.sections.additionalDetailsSection.disclaimer')}
        isLoading={isLoading}
        showDivider={false}
      >
        <span className='font-light text-black-alpha-60'>
          {footprintDetails?.pcf_legal_statement ?? t('global.notSpecified')}
        </span>
      </DetailRow>
    </div>
  );
};

export default AdditionalDetailsSection;
